import { Injectable } from "@angular/core";
import { environment } from "../../../core/config/environment";
import { HttpClient } from "@angular/common/http";
import { catchError, Observable } from "rxjs";
import { BQ7ReportResponseDTO } from "../../reports/models/BQ7ReportResponseDTO";
import { CompleteInspectionJob } from "../../../personas/ccm/BQ7/complete-jobs/complete-jobs.component";
import { LoggerService } from "../../../shared/services/logger.service";
import { CONSTANTS } from "../../../constants";
import { db, SyncableStatus } from "../../../core/database/db";
import { JobWithCountsDto } from "../models/JobWithCountsDto";

type BuildQualityStep = typeof CONSTANTS.BUILD_QUALITY_STEPS[keyof typeof CONSTANTS.BUILD_QUALITY_STEPS];

@Injectable({
  providedIn: "root"
})
export class JobService {

  private readonly baseJobUrl = `${environment.backend.apiBaseURL}/api/Job`;

  private readonly getJobURL =
    `${environment.backend.apiBaseURL}/api/Job/GetBq7JobReport/`;

  private readonly completedBQ7JobsURL =
    `${environment.backend.apiBaseURL}/api/JobSchedule/GetCompletedBQ7Jobs`;

  isOnline: boolean = true;

  constructor(
    private http: HttpClient,
    private logger: LoggerService
  ) {
  }

  getCompletedBQ7Jobs(): Observable<CompleteInspectionJob[]> {
    return this.http.get<CompleteInspectionJob[]>(this.completedBQ7JobsURL).pipe(
      catchError(err => {
        this.logger.error("[getCompletedBQ7Jobs] Error fetching completed jobs", err);
        throw err;
      })
    );
  }

  getJobReport(jobId: number): Observable<BQ7ReportResponseDTO> {
    return this.http.get<BQ7ReportResponseDTO>(this.getJobURL + jobId).pipe(
      catchError(err => {
        this.logger.error(`[getJobReport] Error fetching job report ${jobId}`, err);
        throw err;
      })
    );
  }

  async getJobById(jobId: number) {
    return await db.inspectionJob.get(jobId);
  }

  async getAllJobsForLot(lotId: number) {
    return await db.inspectionJob.where("lotId")
      .equals(lotId)
      .toArray();
  }

  async getAllCompletedJobsForLot(lotId: number) {
    return await db.inspectionJob.where("lotId")
      .equals(lotId)
      .and(x => x.isComplete === true)
      .toArray();
  }

  async getJobForLotByQualityStep(lotId: number, qualityStep: BuildQualityStep) {
    return await db.inspectionJob
      .where("lotId")
      .equals(lotId)
      .and(j => j.buildQualityStep === qualityStep)
      .first();
  }

  getJobWithCounts(jobId: number): Observable<JobWithCountsDto> {
    return this.http.get<JobWithCountsDto>(`${this.baseJobUrl}/${jobId}/counts`).pipe(
      catchError(err => {
        this.logger.error(`[getJobWithCounts] Error fetching job with counts for job ${jobId}`, err);
        throw err;
      })
    );
  }

  async markJobAsComplete(jobId: number, submittedAt: Date): Promise<void> {
    await db.inspectionJob.update(jobId, {
      isComplete: true,
      submittedAt,
      score: undefined,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    });
  }
}
